import React, { useMemo } from 'react';
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { ApolloProvider } from '@apollo/client';

import { getDeployedEnvType, useNextMiApolloClient, getNextMiApolloClient } from '@marriott/mi-apollo-client-utils';
import {
  PageParamsContext,
  getHeroBannerImages,
  getSubDirectoryLocale,
  getUpdatedReservationUrl,
} from '@marriott/mi-rnb-components';
import { catalogRestaurantById } from '@marriott/mi-rnb-graphql';
import { Layout, respGridUtil, prePageCall, getProcessEnvs } from '@marriott/mi-headless-utils';
import {
  constants,
  MIN_PAGE_HEIGHT,
  defaultPageTitle,
  IMAGE_URL_DOMAIN,
  PAGES,
  outletPageUrl,
  outletprimaryType,
  outletsecondaryType,
  OutletCuisines,
} from '@marriott/mi-rnb-components/constants';
import { GetServerSideProps } from 'next';
import operationSignatures from '../../../../_constants/operationSignatures.json';
import Cookies from 'js-cookie';

const { ENV_DEV, NEXT_PUBLIC_AEM_PATH, NEXT_PUBLIC_DEFAULT_LANG = 'en-US' } = process.env;
const isDev = process.env['NODE_ENV'] === ENV_DEV;

const mockModel = async () => {
  if (isDev) {
    // nx-ignore-next-line
    const { outletDetailsModel } = await import('@marriott/mi-rnb-components/localModel');
    return outletDetailsModel;
  }
  return {};
};

const { ITEM_PATH_ROOT, ITEM_PATH_FOOTER, ITEM_PATH_HEADER } = constants;
const { OUTLET_DETAILS_PAGE } = PAGES;
const sessionID = Cookies.get('sessionID');
const currentTimestamp = Date.now();
const currentDateTimeStamp = new Date(currentTimestamp).getTime();

// final data would have to come from 2 calls:
// 1. Page level call from AEM - that will populate the labels on components
// 2. Functional data for components like list of amenities and other related information
// import jsonContent from '../../mock/label-info.json';

export default function OutletDetailsPage({
  model,
  deployedEnvType,
  params,
  isAuthorMode,
  cookies,
  outletData,
  outletDataError,
  requestUri,
  currentLocale,
}) {
  const MiApolloClient = useNextMiApolloClient(operationSignatures, deployedEnvType);

  const responsiveGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);
  const responsiveGridHeaderModel = respGridUtil(model?.cqItems?.root?.[':items']?.['header-responsivegrid']);
  const responsiveGridFooterModel = respGridUtil(model?.cqItems?.root?.[':items']?.['footer-responsivegrid']);
  const campaignParams = model?.cqItems?.root?.[':items']?.responsivegrid?.[':items'].campaignsparams;

  const {
    metaNames,
    metaProperties,
    staticComponentClientLibsJS,
    staticComponentClientLibsCSS,
    gridCss,
    hrefLangList,
    bgColor,
    descriptionWithOtherCuisine,
    descriptionWithCuisine,
  } = model;
  let { title, canonical } = model;
  const marshaId = params['marsha'] ? params['marsha'].split('-')[0] : '';
  const outletDetails = params['outletId'] ? params['outletId'].split('-') : 0;
  const outletId = outletDetails.shift();

  const outletName = outletDetails.join('-');
  const fallbackDescription =
    model?.cqItems?.root?.[':items']?.responsivegrid?.[':items']?.outletinfo?.description || '';
  const fallbackHeroImgPath =
    model?.cqItems?.root?.[':items']?.responsivegrid?.[':items']?.herobannercarousel?.dynamicMedia || {};
  const heroBannerImages = getHeroBannerImages(outletData, fallbackHeroImgPath);

  if (outletData && !!outletData?.basicInformation?.reservationUrl) {
    const updatedUrl = getUpdatedReservationUrl(
      campaignParams,
      outletData?.basicInformation?.reservationUrl,
      outletData?.operationalDetails
    );
    outletData = { ...outletData, basicInformation: { ...outletData?.basicInformation, reservationURL: updatedUrl } };
  }

  const contextValue = useMemo(() => {
    return {
      marsha: marshaId,
      outletId: outletId,
      outletName: outletName,
      fallbackDescription: fallbackDescription,
      fallbackHeroImgPath: fallbackHeroImgPath,
      userId: cookies.UserIdToken,
      currentPage: OUTLET_DETAILS_PAGE,
      backgroundColor: bgColor,
      outletDataResponse: outletData,
      outletDataResponseError: outletDataError,
      heroBannerImages: heroBannerImages,
      currentLocale,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    marshaId,
    outletId,
    outletName,
    fallbackDescription,
    cookies.UserIdToken,
    bgColor,
    outletData,
    outletDataError,
    heroBannerImages,
    currentLocale,
  ]);

  // Function to get venue type value
  const getVenueTypeValue = (primaryType: string | undefined): string => {
    let venueType: string = '';
    if (primaryType) {
      venueType = primaryType || '';
    } else {
      venueType = outletprimaryType?.charAt(0)?.toUpperCase() + outletprimaryType?.slice(1) || '';
    }
    return venueType;
  };

  //  Updating the meta properties and title as per SEO requirements
  const updateMetaProperties = () => {
    const cityName = outletData?.contactInfo?.address?.city || '';
    const outletName = outletData?.basicInformation?.name ? outletData?.basicInformation?.name : '';
    const venueType = getVenueTypeValue(outletData?.basicInformation?.primaryType);
    const dynamicTitle = outletName + ' | ' + cityName + ' ' + venueType + ' | ' + defaultPageTitle.outletDetailsTitle;

    // Title
    if (title) {
      title = dynamicTitle;
    }

    // Description
    // const descriptionKey = metaNames?.find(item => item.name === 'description');
    let description = '';
    if (
      outletData?.diningExperience &&
      outletData?.diningExperience?.cuisines?.length > 0 &&
      outletData?.diningExperience?.cuisines[0] &&
      outletData?.diningExperience?.cuisines[0] !== OutletCuisines.other &&
      outletData?.diningExperience?.cuisines[0] !== OutletCuisines.multicuisine
    ) {
      description = descriptionWithCuisine;
    } else {
      description = descriptionWithOtherCuisine;
    }
    if (description?.includes('[Restaurant Name]')) {
      description = updateOutletInDesc(description);
    }
    if (description?.includes('[city]')) {
      description = updateCityInDesc(description);
    }
    if (description?.includes('[primary restaurant/bar type]')) {
      description = updateVenueTypeInDesc(description, outletData?.basicInformation?.primaryType || outletprimaryType);
    }
    if (description?.includes('[Restaurant cuisine]')) {
      description = updateCuisineInDesc(
        description,
        outletData?.diningExperience?.cuisines ? outletData?.diningExperience?.cuisines[0] : ''
      );
    }
    if (description?.includes('[Hotel Name]')) {
      description = updateHotelNameInDesc(
        description,
        outletData?.properties?.edges[0]?.node?.property?.basicInformation?.displayName
          ? outletData?.properties?.edges[0]?.node?.property?.basicInformation?.displayName
          : ''
      );
    }

    // Update Meta Names
    metaNames?.map(item => {
      if (item.name === 'description') {
        item.value = description;
      }
      if (item?.name === 'url') {
        const urlSplit = item?.value.split('//');
        const hostUrl = urlSplit?.[1]?.split('/');
        item.value = urlSplit?.[0] + '//' + hostUrl?.[0] + '/' + hostUrl?.[1] + requestUri + '.mi';
      }
    });

    // Update Meta Properties
    metaProperties?.map(item => {
      if (item?.name === 'og:title') item.value = dynamicTitle;

      if (item?.name === 'og:description') {
        item.value = description;
      }

      if (item?.name === 'og:url') {
        const urlSplit = item?.value.split('//');
        const hostUrl = urlSplit?.[1]?.split('/');
        item.value = urlSplit?.[0] + '//' + hostUrl?.[0] + '/' + hostUrl?.[1] + requestUri + '.mi';
      }
      if (item?.name === 'og:image') {
        if (heroBannerImages[0]?.isFallBackImage) item.value = heroBannerImages[0]?.dynamicMedia?.assetPath;
        else item.value = heroBannerImages[0]?.defaultImageUrl;
      }
    });
  };

  const updateOutletInDesc = (description: string) => {
    const filledDescription = description.replace('[Restaurant Name]', outletData?.basicInformation?.name);
    return filledDescription;
  };

  const updateCityInDesc = (description: string) => {
    const cityDescprition = outletData?.contactInfo?.address?.city ? outletData?.contactInfo?.address?.city : '';
    const filledDescription = description.replace('[city]', cityDescprition);
    return filledDescription;
  };

  const updateVenueTypeInDesc = (description: string, venueType: string) => {
    const filledDescription = description.replace('[primary restaurant/bar type]', venueType);
    return filledDescription;
  };

  const updateCuisineInDesc = (description: string, cuisineType: string) => {
    const filledDescription = description.replace('[Restaurant cuisine]', cuisineType);
    return filledDescription;
  };

  const updateHotelNameInDesc = (description: string, hotelName: string) => {
    const filledDescription = description.replace('[Hotel Name]', hotelName);
    return filledDescription;
  };

  const updateCanonicalValue = () => {
    const urlSplit = canonical?.split('//');
    const hostUrl = urlSplit?.[1]?.split('/');
    canonical = urlSplit?.[0] + '//' + hostUrl?.[0] + '/' + hostUrl?.[1] + requestUri + '.mi';
  };

  // Update hrefLangList URL
  const updateHrefLangList = () => {
    hrefLangList?.map(hrefLangItem => {
      const urlSplit = canonical?.split('//');
      const protocol = urlSplit?.[0] || 'https:';
      const hostUrl = urlSplit?.[1]?.split('/');
      const langCode =
        hrefLangItem?.hreflang === 'x-default' ? NEXT_PUBLIC_DEFAULT_LANG.toLowerCase() : hrefLangItem?.hreflang;
      hrefLangItem.href = protocol + '//' + hostUrl?.[0] + '/' + langCode + requestUri + '.mi';
    });
  };

  updateMetaProperties();
  updateCanonicalValue();
  updateHrefLangList();

  return (
    <ApolloProvider client={MiApolloClient}>
      <PageParamsContext.Provider value={contextValue}>
        <Layout
          metaNames={metaNames}
          metaProps={metaProperties}
          title={title}
          staticComponentClientLibsCSS={staticComponentClientLibsCSS}
          staticComponentClientLibsJS={staticComponentClientLibsJS}
          isAuthorMode={isAuthorMode}
          gridCss={gridCss}
          canonical={canonical}
          hreflangList={hrefLangList}
        >
          <section>
            {responsiveGridHeaderModel && (
              <ResponsiveGrid
                {...responsiveGridHeaderModel}
                model={responsiveGridHeaderModel}
                pagePath={NEXT_PUBLIC_AEM_PATH}
                itemPath={ITEM_PATH_HEADER}
              />
            )}
            <div style={{ minHeight: MIN_PAGE_HEIGHT, clear: 'both' }} className="color-scheme1">
              <ResponsiveGrid
                {...responsiveGridModel}
                model={responsiveGridModel}
                pagePath={NEXT_PUBLIC_AEM_PATH}
                itemPath={ITEM_PATH_ROOT}
              />
            </div>
            {responsiveGridFooterModel && (
              <ResponsiveGrid
                {...responsiveGridFooterModel}
                model={responsiveGridFooterModel}
                pagePath={NEXT_PUBLIC_AEM_PATH}
                itemPath={ITEM_PATH_FOOTER}
              />
            )}
          </section>
        </Layout>
      </PageParamsContext.Provider>
    </ApolloProvider>
  );
}

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rnbSpecificData = async (context: { [key: string]: any }, params: any) => {
  const currentLocale = context?.req?.['headers']?.['accept-language'] ?? 'en-US';
  const marshaId = context?.params['marsha'] ? context?.params['marsha'].split('-')[0].toUpperCase() : '';
  const outletDetails = context?.params['outletId'] ? context?.params['outletId'].split('-') : 0;
  const outletId = outletDetails.shift();
  //any page specific code can be written here.
  const deployedEnvType = getDeployedEnvType();
  const requestId = `${Date.now()}`;
  const MiApolloClient = getNextMiApolloClient(operationSignatures, deployedEnvType);
  let response;
  const subLocale = getSubDirectoryLocale(currentLocale || 'en-US');
  const { GOOGLE_MAP_API_KEY } = getProcessEnvs();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const headers: any = {
    'x-request-id': requestId,
    'accept-language': currentLocale,
    'x-b3-traceId': requestId !== '' ? requestId : `${currentDateTimeStamp}`,
    'x-b3-spanId': requestId !== '' ? requestId : `${currentDateTimeStamp}`,
    'correlation-id': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
  };

  if (deployedEnvType === 'lower') {
    headers['Accept-Encoding'] = 'gzip, deflate, br, zstd';
  }

  try {
    const { data } = await MiApolloClient.query({
      query: catalogRestaurantById,
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
      variables: {
        propertyId: marshaId,
        restaurantId: Number(outletId),
        version: 'V2',
        architecturalServicesAssetPlacement: true,
        propertiesFilter: {
          properties: [marshaId],
        },
      },
      context: {
        headers: headers,
      },
    });

    response = { GOOGLE_MAP_API_KEY };

    if (data) {
      const propertyName = data?.restaurantById?.properties?.edges[0]?.node?.property?.seoNickname || marshaId;
      const outletName = data?.restaurantById?.basicInformation?.name;
      const generatedUrl =
        propertyName
          ?.replace(/[^A-Z0-9]+/gi, '-')
          ?.replace(/^-+|-+$/g, '')
          ?.toLowerCase() +
        '/' +
        outletId +
        '-' +
        outletName
          ?.replace(/[^A-Z0-9]+/gi, '-')
          ?.replace(/^-+|-+$/g, '')
          ?.toLowerCase();

      const accessedUrlWithParams = context?.resolvedUrl?.split(outletPageUrl)[1];
      const accessedUrl = accessedUrlWithParams?.split('?')[0];
      const params = accessedUrlWithParams?.split('?')?.length > 1 ? '?' + accessedUrlWithParams?.split('?')[1] : '';
      if (generatedUrl == accessedUrl) {
        response = {
          ...response,
          operationSignatures: operationSignatures,
          deployedEnvType,
          outletData: data?.restaurantById,
          outletId: outletId,
          outletDataError: false,
          //  pageUrl: params?.requestUri || '',
          showLdJson: true,
        };
      } else {
        context.res.writeHead(301, { Location: `${subLocale}${outletPageUrl}${generatedUrl}.mi${params}` });
        context.res.end();

        response = { ...response, operationSignatures: operationSignatures, deployedEnvType };
      }
      return response;
    }
  } catch (error) {
    return {
      operationSignatures: operationSignatures,
      deployedEnvType,
      outletData: null,
      outletDataError: true,
      pageUrl: params?.requestUri || '',
    };
  }
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getServerSideProps: GetServerSideProps<any> = prePageCall(rnbSpecificData, mockModel, false, true, true);
